/**
 * Minimal three-box3d world for the Texture Lab drop test.
 * One static ground, optional static wall, ~12 dynamic boxes. No fracture.
 */
import {
  createBox3D,
  FixedStepper,
  TransformBuffer,
  SleepManager,
  type Box3D,
  type World,
  type BodyHandle,
} from 'box3d-web';

export const TEXLAB_FIXED_DT = 1 / 60;
export const TEXLAB_SUBSTEPS = 4;
export const TEXLAB_DROP_COUNT = 12;
export const TEXLAB_BOX_HALF = 0.09;

export interface TextureLabDropRuntime {
  world: World;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  sleep: SleepManager;
  ground: BodyHandle;
  wall: BodyHandle | null;
  dropBodies: BodyHandle[];
  transforms: Float32Array;
  simTime: number;
  awakeCount: number;
}

let modulePromise: Promise<Box3D> | null = null;

function loadModule() {
  if (!modulePromise) modulePromise = createBox3D();
  return modulePromise;
}

export async function createTextureLabDropWorld(): Promise<TextureLabDropRuntime> {
  const box3d = await loadModule();
  const world = box3d.createWorld({ gravity: [0, -9.8, 0] });
  const ground = world.createBody({ type: 'static', position: [0, -0.4, 0] });
  world.addBox(ground, [40, 0.4, 40], { density: 0, friction: 0.92, restitution: 0.02 });

  const buffer = new TransformBuffer(TEXLAB_DROP_COUNT + 4);
  const sleep = new SleepManager(world, {
    settleSteps: 2,
    sweepIntervalSec: 0.5,
    moveThreshold: 0.01,
  });

  return {
    world,
    stepper: new FixedStepper({ fixedDt: TEXLAB_FIXED_DT, substeps: TEXLAB_SUBSTEPS, maxStepsPerFrame: 4 }),
    buffer,
    sleep,
    ground,
    wall: null,
    dropBodies: [],
    transforms: new Float32Array(0),
    simTime: 0,
    awakeCount: 0,
  };
}

export function setTextureLabWallCollider(
  rt: TextureLabDropRuntime,
  wall: { w: number; h: number; t: number },
  yBottom: number,
): void {
  if (rt.wall != null) {
    rt.world.destroyBody(rt.wall);
    rt.wall = null;
  }
  const body = rt.world.createBody({
    type: 'static',
    position: [0, yBottom + wall.h / 2, 0],
  });
  rt.world.addBox(body, [wall.w / 2, wall.h / 2, wall.t / 2], {
    density: 0,
    friction: 0.85,
    restitution: 0.02,
  });
  rt.wall = body;
}

export function clearTextureLabDrops(rt: TextureLabDropRuntime): void {
  for (const body of rt.dropBodies) rt.world.destroyBody(body);
  rt.dropBodies = [];
  rebuildDropBuffer(rt);
  rt.awakeCount = 0;
}

export function spawnTextureLabDrops(
  rt: TextureLabDropRuntime,
  opts: { density: number; friction: number; restitution: number; seed: number },
): void {
  clearTextureLabDrops(rt);
  const cols = 4;
  for (let i = 0; i < TEXLAB_DROP_COUNT; i += 1) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const jitter = seededUnit(opts.seed * 17 + i * 13);
    const x = (col - (cols - 1) / 2) * 0.28 + (jitter - 0.5) * 0.06;
    const y = 2.55 + row * 0.28;
    const z = 0.95 + (seededUnit(opts.seed * 31 + i * 7) - 0.5) * 0.2;
    const body = rt.world.createBody({ type: 'dynamic', position: [x, y, z] });
    rt.world.addBox(body, [TEXLAB_BOX_HALF, TEXLAB_BOX_HALF, TEXLAB_BOX_HALF], {
      density: Math.max(opts.density, 1),
      friction: opts.friction,
      restitution: opts.restitution,
    });
    rt.dropBodies.push(body);
  }
  rebuildDropBuffer(rt);
  // Bodies spawn in air — do not force-sleep. Sweep will sleep them after they settle.
  rt.sleep.watch(rt.dropBodies, rt.buffer);
}

export function advanceTextureLabDrop(rt: TextureLabDropRuntime, delta: number): void {
  const stepped = rt.stepper.advance(delta, () => {
    rt.world.step(TEXLAB_FIXED_DT, TEXLAB_SUBSTEPS);
  });
  if (!stepped) return;
  rt.simTime = rt.stepper.simTime;
  if (rt.dropBodies.length === 0) {
    rt.awakeCount = 0;
    return;
  }
  rt.buffer.readInto(rt.world);
  rt.transforms = rt.buffer.transforms;
  rt.sleep.sweep(rt.simTime);
  let awake = 0;
  for (const body of rt.dropBodies) {
    if (rt.world.isBodyAwake(body)) awake += 1;
  }
  rt.awakeCount = awake;
}

export function disposeTextureLabDropWorld(rt: TextureLabDropRuntime): void {
  clearTextureLabDrops(rt);
  if (rt.wall != null) rt.world.destroyBody(rt.wall);
  rt.world.destroyBody(rt.ground);
  rt.world.destroy();
}

function rebuildDropBuffer(rt: TextureLabDropRuntime): void {
  rt.buffer = new TransformBuffer(Math.max(rt.dropBodies.length, 1));
  for (const body of rt.dropBodies) rt.buffer.add(body);
  rt.buffer.rebuild();
  rt.transforms = rt.buffer.transforms;
}

function seededUnit(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}
